import {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { authApi } from '../../../assets/api';
import {
  getAvailableVerificationChannels,
  VERIFICATION_CHANNEL_MAP,
} from '../../../assets/constants/verificationChannels';
import { CurrentUserContext } from '../../../assets/contexts/currentUserContext';
import { TranslationContext } from '../../../assets/contexts/translationContext';
import { clearPasscode, getRegistration } from '../../../assets/services/auth';
import { triggerNotificationHaptic } from '../../../assets/utils/hapticFeedback';
import BottomActionButton from '../../shared/BottomActionButton/BottomActionButton';
import ScreenHeader from '../../ScreenHeader/ScreenHeader';
import PinPage from '../PinPage/PinPage';
import './ForgotPin.css';

const FORGOT_PIN_STEP = {
  CHANNEL: 'channel',
  CODE: 'code',
};
const CODE_LENGTH = 6;
const CODE_ERROR_HIGHLIGHT_DURATION = 1200;
const REQUEST_COOLDOWN_MS = 60 * 1000;
const PASSWORD_REQUEST_GENERATED_UTC_KEY = 'obank_forgot_pin_password_generated_utc';
const CODE_ERROR_SHAKE_ANIMATION = { x: [0, -8, 8, -6, 6, -3, 3, 0] };
const CODE_ERROR_SHAKE_TRANSITION = { duration: 0.42, ease: 'easeInOut' };

function stringifyApiErrorValue(value) {
  if (!value) {
    return null;
  }

  if (typeof value === 'string') {
    return value;
  }

  if (Array.isArray(value)) {
    return value
      .map((item) => stringifyApiErrorValue(item))
      .filter(Boolean)
      .join('\n');
  }

  if (typeof value === 'object') {
    if (typeof value.msg === 'string') {
      return value.msg;
    }

    if (typeof value.message === 'string') {
      return value.message;
    }

    if (typeof value.detail === 'string') {
      return value.detail;
    }

    return null;
  }

  return String(value);
}

function getApiErrorMessage(error, fallback) {
  return (
    stringifyApiErrorValue(error?.detail)
    || stringifyApiErrorValue(error?.payload?.detail)
    || stringifyApiErrorValue(error?.payload)
    || stringifyApiErrorValue(error?.message)
    || fallback
  );
}

function isMaxTriesExceededError(error) {
  const message = getApiErrorMessage(error, '');

  return message.trim().toLowerCase() === 'max tries exceeded';
}

function compactObject(payload) {
  return Object.fromEntries(
    Object.entries(payload).filter(([, value]) => value !== undefined && value !== null)
  );
}

function normalizeUtcValue(value) {
  const normalizedValue = String(value || '').trim();

  if (!normalizedValue) {
    return '';
  }

  return /(?:z|[+-]\d{2}:?\d{2})$/i.test(normalizedValue)
    ? normalizedValue
    : `${normalizedValue}Z`;
}

function parseUtcMs(value) {
  if (!value) {
    return 0;
  }

  const parsedMs = Date.parse(normalizeUtcValue(value));

  return Number.isFinite(parsedMs) ? parsedMs : 0;
}

function getLatestRequestGeneratedUtc(currentValue, nextValue) {
  return parseUtcMs(nextValue) > parseUtcMs(currentValue) ? nextValue : currentValue;
}

function getStoredPasswordRequestGeneratedUtc() {
  try {
    return sessionStorage.getItem(PASSWORD_REQUEST_GENERATED_UTC_KEY) || '';
  } catch {
    return '';
  }
}

function savePasswordRequestGeneratedUtc(generatedUtc) {
  if (!generatedUtc) {
    return;
  }

  try {
    sessionStorage.setItem(PASSWORD_REQUEST_GENERATED_UTC_KEY, generatedUtc);
  } catch {
    // Ignore storage failures. Cooldown still works for the current render.
  }
}

function clearPasswordRequestGeneratedUtc() {
  try {
    sessionStorage.removeItem(PASSWORD_REQUEST_GENERATED_UTC_KEY);
  } catch {
    // Ignore storage failures.
  }
}

function getRequestCooldownEndMs(generatedUtc) {
  const generatedMs = parseUtcMs(generatedUtc);

  return generatedMs ? generatedMs + REQUEST_COOLDOWN_MS : 0;
}

function getRequestCooldownRemainingSeconds(generatedUtc, nowMs = Date.now()) {
  const endMs = getRequestCooldownEndMs(generatedUtc);

  if (!endMs) {
    return 0;
  }

  return Math.max(Math.ceil((endMs - nowMs) / 1000), 0);
}

function formatRequestCooldown(remainingSeconds, common) {
  const seconds = Math.max(Math.ceil(Number(remainingSeconds) || 0), 0);

  if (seconds === 1) {
    return `1 ${common.second}`;
  }

  return `${seconds} ${common.seconds}`;
}

function useRequestCooldown(generatedUtc) {
  const [nowMs, setNowMs] = useState(() => Date.now());
  const cooldownEndMs = useMemo(() => getRequestCooldownEndMs(generatedUtc), [generatedUtc]);
  const remainingSeconds = getRequestCooldownRemainingSeconds(generatedUtc, nowMs);

  useEffect(() => {
    setNowMs(Date.now());

    if (!cooldownEndMs || cooldownEndMs <= Date.now()) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      const nextNowMs = Date.now();

      setNowMs(nextNowMs);

      if (nextNowMs >= cooldownEndMs) {
        window.clearInterval(intervalId);
      }
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [cooldownEndMs]);

  return {
    cooldownEndMs,
    isCooldownActive: remainingSeconds > 0,
    remainingSeconds,
  };
}

function getFallbackRecoveryUser() {
  const registration = getRegistration();

  if (!registration) {
    return null;
  }

  return {
    ...registration.currentUser,
    ...registration.forgotPasswordUser,
    token:
      registration.currentUser?.token
      ?? registration.forgotPasswordUser?.token
      ?? registration.token
      ?? registration.forgotPasswordToken,
    email:
      registration.currentUser?.email
      ?? registration.forgotPasswordUser?.email
      ?? registration.email,
    phone:
      registration.currentUser?.phone
      ?? registration.forgotPasswordUser?.phone
      ?? registration.phone
      ?? registration.phoneNumber,
  };
}

function getRecoveryToken(user) {
  return user?.token ?? user?.forgot_password_token ?? user?.forgotPasswordToken ?? null;
}

function getRecoveryPasswordGeneratedUtc(user) {
  return user?.reg_data?.new_password?.generated_utc
    ?? user?.default_organization?.reg_data?.new_password?.generated_utc
    ?? '';
}

function getRecoveryPasswordGeneratedUtcFromResponse(response) {
  return response?.reg_data?.new_password?.generated_utc
    ?? response?.default_organization?.reg_data?.new_password?.generated_utc
    ?? response?.new_password?.generated_utc
    ?? response?.generated_utc
    ?? response?.generatedUtc
    ?? '';
}

function mergeRecoveryPasswordGeneratedUtc(user, generatedUtc) {
  if (!user || !generatedUtc) {
    return user;
  }

  const currentGeneratedUtc = getRecoveryPasswordGeneratedUtc(user);

  if (
    currentGeneratedUtc === generatedUtc
    || getLatestRequestGeneratedUtc(currentGeneratedUtc, generatedUtc) !== generatedUtc
  ) {
    return user;
  }

  return {
    ...user,
    reg_data: {
      ...(user.reg_data ?? {}),
      new_password: {
        ...(user.reg_data?.new_password ?? {}),
        generated_utc: generatedUtc,
      },
    },
  };
}

function buildPasswordResetSetPinUser(nextUser, fallbackUser) {
  const nextRegData = nextUser?.reg_data ?? {};
  const fallbackRegData = fallbackUser?.reg_data ?? {};
  const nextRegDataWithoutNewPassword = { ...nextRegData };
  const fallbackRegDataWithoutNewPassword = { ...fallbackRegData };

  delete nextRegDataWithoutNewPassword.new_password;
  delete fallbackRegDataWithoutNewPassword.new_password;

  return {
    ...(fallbackUser ?? {}),
    ...(nextUser ?? {}),
    reg_data: {
      ...fallbackRegDataWithoutNewPassword,
      ...nextRegDataWithoutNewPassword,
      new_password: null,
      stages: {
        ...(fallbackRegData.stages ?? {}),
        ...(nextRegData.stages ?? {}),
        is_system_allowed: true,
        is_password_set: false,
      },
    },
  };
}

function getRecoveryEmail(user) {
  return user?.email ?? user?.default_organization?.email ?? null;
}

function getRecoveryPhone(user) {
  return user?.phone ?? user?.phoneNumber ?? user?.default_organization?.phone ?? null;
}

function getReturnUrl() {
  if (typeof window === 'undefined') {
    return undefined;
  }

  return window.location.href;
}

function maskEmail(email) {
  if (!email || !email.includes('@')) {
    return email;
  }

  const [name, domain] = email.split('@');
  const visibleName = name.slice(0, 2);

  return `${visibleName}${name.length > 2 ? '***' : '*'}@${domain}`;
}

function maskPhone(phone) {
  if (!phone) {
    return phone;
  }

  const normalized = String(phone);
  const suffix = normalized.slice(-4);

  return `${normalized.startsWith('+') ? '+' : ''}*** ${suffix}`;
}

function focusInput(input) {
  if (!input) {
    return false;
  }

  try {
    input.focus({ preventScroll: true });
  } catch {
    input.focus();
  }

  input.select();

  return typeof document === 'undefined' ? true : document.activeElement === input;
}

function ForgotPinCodeInput({
  ariaLabel = '',
  autoFocus = true,
  disabled = false,
  errorKey = 0,
  hasError = false,
  onChange,
  onComplete,
  readOnly = false,
  value,
}) {
  const nativeInputRef = useRef(null);
  const [isErrorActive, setIsErrorActive] = useState(false);
  const [hasNativeFocus, setHasNativeFocus] = useState(false);
  const shouldReduceMotion = useReducedMotion();
  const code = useMemo(
    () => Array.from({ length: CODE_LENGTH }, (_, index) => value[index] ?? ''),
    [value]
  );
  const activeIndex = Math.min(value.length, CODE_LENGTH - 1);
  const canFocusInput = autoFocus && !disabled;

  const focusNativeInput = useCallback(() => {
    if (!canFocusInput) {
      return false;
    }

    return focusInput(nativeInputRef.current);
  }, [canFocusInput]);

  const handleChange = useCallback(
    (event) => {
      onChange(event.target.value.replace(/\D/g, '').slice(0, CODE_LENGTH));
    },
    [onChange]
  );

  const handlePointerDown = useCallback((event) => {
    if (disabled) {
      return;
    }

    event.preventDefault();
    focusNativeInput();
  }, [disabled, focusNativeInput]);

  useEffect(() => {
    if (!hasError) {
      setIsErrorActive(false);
      return undefined;
    }

    triggerNotificationHaptic('error');
    setIsErrorActive(true);
    const timeoutId = window.setTimeout(() => {
      setIsErrorActive(false);
    }, CODE_ERROR_HIGHLIGHT_DURATION);

    return () => window.clearTimeout(timeoutId);
  }, [errorKey, hasError]);

  useEffect(() => {
    if (value.length !== CODE_LENGTH || disabled || readOnly) {
      return;
    }

    onComplete(value);
  }, [disabled, onComplete, readOnly, value]);

  useLayoutEffect(() => {
    if (!value) {
      focusNativeInput();
    }
  }, [focusNativeInput, value]);

  useEffect(() => {
    if (!canFocusInput || value) {
      return undefined;
    }

    const timeoutIds = [];
    let animationFrameId = null;

    focusNativeInput();

    if (typeof window.requestAnimationFrame === 'function') {
      animationFrameId = window.requestAnimationFrame(() => {
        focusNativeInput();
      });
    }

    [40, 120, 280].forEach((delay) => {
      timeoutIds.push(window.setTimeout(() => {
        focusNativeInput();
      }, delay));
    });

    return () => {
      if (animationFrameId !== null && typeof window.cancelAnimationFrame === 'function') {
        window.cancelAnimationFrame(animationFrameId);
      }

      timeoutIds.forEach((timeoutId) => {
        window.clearTimeout(timeoutId);
      });
    };
  }, [canFocusInput, focusNativeInput, value]);

  return (
    <motion.div
      className={`forgot-pin__code ${isErrorActive ? 'forgot-pin__code--error' : ''}`}
      animate={isErrorActive && !shouldReduceMotion ? CODE_ERROR_SHAKE_ANIMATION : { x: 0 }}
      transition={CODE_ERROR_SHAKE_TRANSITION}
    >
      <div
        className="forgot-pin__code-row"
        role="group"
        aria-label={`${ariaLabel} fields`}
        onPointerDown={handlePointerDown}
      >
        <input
          className="forgot-pin__code-native-input"
          ref={nativeInputRef}
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          enterKeyHint="done"
          maxLength={CODE_LENGTH}
          value={value}
          disabled={disabled}
          readOnly={readOnly}
          aria-label={ariaLabel}
          autoFocus={autoFocus}
          onChange={handleChange}
          onFocus={() => setHasNativeFocus(true)}
          onBlur={() => setHasNativeFocus(false)}
        />
        {code.map((digit, index) => (
          <span
            className={[
              'forgot-pin__code-cell-wrap',
              hasNativeFocus && !disabled && index === activeIndex
                ? 'forgot-pin__code-cell-wrap--active'
                : null,
            ]
              .filter(Boolean)
              .join(' ')}
            key={index}
            aria-hidden="true"
          >
            <span
              className={[
                'forgot-pin__code-cell',
                hasNativeFocus && !disabled && !readOnly && index === activeIndex && !digit
                  ? 'forgot-pin__code-cell--cursor'
                  : null,
              ]
                .filter(Boolean)
                .join(' ')}
            >
              {digit}
            </span>
          </span>
        ))}
      </div>
    </motion.div>
  );
}

function ForgotPin({ authService = authApi }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, setCurrentUser } = useContext(CurrentUserContext);
  const { channels, common, errors, pin, t } = useContext(TranslationContext);
  const isLoggedInRecovery = location.state?.loggedInRecovery === true;
  const backPath = location.state?.backPath || '/profile/security/passcode';
  const recoveryUser = currentUser ?? location.state?.user ?? getFallbackRecoveryUser();
  const token = getRecoveryToken(recoveryUser);
  const email = getRecoveryEmail(recoveryUser);
  const phone = getRecoveryPhone(recoveryUser);
  const recoveryPasswordGeneratedUtc = getRecoveryPasswordGeneratedUtc(recoveryUser);
  const availableChannels = useMemo(
    () => getAvailableVerificationChannels({ email, phone }),
    [email, phone]
  );
  const [step, setStep] = useState(FORGOT_PIN_STEP.CHANNEL);
  const [selectedChannel, setSelectedChannel] = useState(availableChannels[0] ?? null);
  const [code, setCode] = useState('');
  const [isCodeInvalidHighlighted, setIsCodeInvalidHighlighted] = useState(false);
  const [codeErrorPulse, setCodeErrorPulse] = useState(0);
  const [passwordRequestGeneratedUtc, setPasswordRequestGeneratedUtc] = useState(() => (
    getLatestRequestGeneratedUtc(
      getStoredPasswordRequestGeneratedUtc(),
      recoveryPasswordGeneratedUtc
    )
  ));
  const [isRequestingCode, setIsRequestingCode] = useState(false);
  const [isCheckingCode, setIsCheckingCode] = useState(false);
  const [isCodeLocked, setIsCodeLocked] = useState(false);
  const codeInvalidTimerRef = useRef(null);
  const isCheckingCodeRef = useRef(false);
  const isRequestingCodeRef = useRef(false);
  const keyboardPrimeInputRef = useRef(null);
  const passwordRequestCooldown = useRequestCooldown(passwordRequestGeneratedUtc);
  const cooldownText = formatRequestCooldown(passwordRequestCooldown.remainingSeconds, common);
  const selectedChannelConfig = selectedChannel
    ? VERIFICATION_CHANNEL_MAP[selectedChannel]
    : null;
  const selectedChannelCopy = selectedChannel ? channels[selectedChannel] : null;
  const selectedChannelLabel = selectedChannelCopy?.label || selectedChannelConfig?.label || '';
  const selectedTarget = selectedChannelConfig?.target === 'email'
    ? maskEmail(email)
    : maskPhone(phone);
  const canRequestCode = Boolean(
    selectedChannel && !isRequestingCode && !passwordRequestCooldown.isCooldownActive
  );
  const isChannelSelectionDisabled = isRequestingCode || passwordRequestCooldown.isCooldownActive;
  const isResendDisabled = isRequestingCode
    || isCheckingCode
    || passwordRequestCooldown.isCooldownActive;

  const clearCodeError = useCallback(() => {
    if (codeInvalidTimerRef.current) {
      window.clearTimeout(codeInvalidTimerRef.current);
      codeInvalidTimerRef.current = null;
    }

    setIsCodeInvalidHighlighted(false);
  }, []);

  const markCodeInvalid = useCallback(() => {
    setCode('');
    setCodeErrorPulse((currentPulse) => currentPulse + 1);
    setIsCodeInvalidHighlighted(true);

    if (codeInvalidTimerRef.current) {
      window.clearTimeout(codeInvalidTimerRef.current);
    }

    codeInvalidTimerRef.current = window.setTimeout(() => {
      setIsCodeInvalidHighlighted(false);
      codeInvalidTimerRef.current = null;
    }, CODE_ERROR_HIGHLIGHT_DURATION);
  }, []);

  useEffect(() => {
    if (!selectedChannel && availableChannels.length > 0) {
      setSelectedChannel(availableChannels[0]);
    }
  }, [availableChannels, selectedChannel]);

  useEffect(() => {
    isCheckingCodeRef.current = isCheckingCode;
  }, [isCheckingCode]);

  useEffect(() => {
    isRequestingCodeRef.current = isRequestingCode;
  }, [isRequestingCode]);

  useEffect(() => () => {
    if (codeInvalidTimerRef.current) {
      window.clearTimeout(codeInvalidTimerRef.current);
    }
  }, []);

  useEffect(() => {
    setPasswordRequestGeneratedUtc((currentValue) => (
      getLatestRequestGeneratedUtc(currentValue, recoveryPasswordGeneratedUtc)
    ));
  }, [recoveryPasswordGeneratedUtc]);

  useEffect(() => {
    savePasswordRequestGeneratedUtc(passwordRequestGeneratedUtc);
  }, [passwordRequestGeneratedUtc]);

  useEffect(() => {
    if (
      !passwordRequestGeneratedUtc
      || recoveryPasswordGeneratedUtc === passwordRequestGeneratedUtc
      || getLatestRequestGeneratedUtc(
        recoveryPasswordGeneratedUtc,
        passwordRequestGeneratedUtc
      ) !== passwordRequestGeneratedUtc
    ) {
      return;
    }

    if (!recoveryUser) {
      return;
    }

    setCurrentUser((prevUser) => (
      mergeRecoveryPasswordGeneratedUtc(prevUser ?? recoveryUser, passwordRequestGeneratedUtc)
    ));
  }, [
    passwordRequestGeneratedUtc,
    recoveryPasswordGeneratedUtc,
    recoveryUser,
    setCurrentUser,
  ]);

  const commitPasswordRequestGeneratedUtc = useCallback(
    (generatedUtc, responseUser) => {
      if (!generatedUtc) {
        return;
      }

      setPasswordRequestGeneratedUtc((currentValue) => (
        getLatestRequestGeneratedUtc(currentValue, generatedUtc)
      ));
      setCurrentUser((prevUser) => (
        mergeRecoveryPasswordGeneratedUtc(
          prevUser ?? recoveryUser ?? responseUser,
          generatedUtc
        )
      ));
    },
    [recoveryUser, setCurrentUser]
  );

  const requestCode = useCallback(
    async (channel) => {
      if (!channel || passwordRequestCooldown.isCooldownActive || isRequestingCodeRef.current) {
        return;
      }

      isRequestingCodeRef.current = true;
      setIsRequestingCode(true);

      try {
        const response = isLoggedInRecovery
          ? await authService.recoverPassword({ channel })
          : await authService.recoverPasswordNotLoggedIn(compactObject({
            channel,
            token,
            returnUrl: getReturnUrl(),
          }));
        const generatedUtc = getRecoveryPasswordGeneratedUtcFromResponse(response)
          || new Date().toISOString();

        commitPasswordRequestGeneratedUtc(generatedUtc, response);
        setSelectedChannel(channel);
        clearCodeError();
        setIsCodeLocked(false);
        setCode('');
        setStep(FORGOT_PIN_STEP.CODE);
      } catch (requestError) {
        const generatedUtc = getRecoveryPasswordGeneratedUtcFromResponse(requestError?.payload)
          || getRecoveryPasswordGeneratedUtcFromResponse(requestError);

        if (generatedUtc) {
          commitPasswordRequestGeneratedUtc(generatedUtc, requestError?.payload);
        }

        toast.error(getApiErrorMessage(requestError, errors.failedSendCode));
      } finally {
        isRequestingCodeRef.current = false;
        setIsRequestingCode(false);
      }
    },
    [
      authService,
      clearCodeError,
      commitPasswordRequestGeneratedUtc,
      errors.failedSendCode,
      isLoggedInRecovery,
      passwordRequestCooldown.isCooldownActive,
      token,
    ]
  );

  const primeCodeKeyboard = useCallback(() => {
    focusInput(keyboardPrimeInputRef.current);
  }, []);

  const handleRequestCode = useCallback(() => {
    primeCodeKeyboard();
    requestCode(selectedChannel);
  }, [primeCodeKeyboard, requestCode, selectedChannel]);

  const handleRequestCodeGesture = useCallback((event) => {
    if (!canRequestCode) {
      return;
    }

    event?.preventDefault?.();
    handleRequestCode();
  }, [canRequestCode, handleRequestCode]);

  const handleSelectChannel = useCallback(
    (channel) => {
      if (isChannelSelectionDisabled) {
        return;
      }

      setSelectedChannel(channel);
    },
    [isChannelSelectionDisabled]
  );

  const handleCodeChange = useCallback((nextCode) => {
    clearCodeError();
    setCode(nextCode.replace(/\D/g, '').slice(0, CODE_LENGTH));
  }, [clearCodeError]);

  const handleConfirmCode = useCallback(async (confirmedCode) => {
    const normalizedCode = String(confirmedCode || '').replace(/\D/g, '').slice(0, CODE_LENGTH);
    let didConfirmCode = false;

    if (
      normalizedCode.length !== CODE_LENGTH
      || isCheckingCodeRef.current
      || isCodeLocked
    ) {
      return;
    }

    isCheckingCodeRef.current = true;
    setIsCheckingCode(true);

    try {
      const payload = {
        code: normalizedCode,
        token,
        returnUrl: getReturnUrl(),
      };
      const nextCurrentUser = isLoggedInRecovery
        ? await authService.validatePasswordResetCode({ code: normalizedCode })
        : await authService.validatePasswordResetCodeNotLoggedIn(compactObject(payload));
      const passwordResetUser = buildPasswordResetSetPinUser(nextCurrentUser, recoveryUser);

      clearPasscode();
      clearPasswordRequestGeneratedUtc();
      setPasswordRequestGeneratedUtc('');
      setCode('');
      setCurrentUser(passwordResetUser);
      didConfirmCode = true;
      navigate('/onboarding/passcode', { replace: true, state: { fromForgotPin: true } });
    } catch (confirmError) {
      const isMaxTriesExceeded = isMaxTriesExceededError(confirmError);
      toast.error(getApiErrorMessage(
        confirmError,
        isMaxTriesExceeded ? pin.tooManyCodeAttempts : pin.invalidCode
      ));
      markCodeInvalid();

      if (isMaxTriesExceeded) {
        setIsCodeLocked(true);
      }
    } finally {
      if (!didConfirmCode) {
        isCheckingCodeRef.current = false;
        setIsCheckingCode(false);
      }
    }
  }, [
    authService,
    markCodeInvalid,
    navigate,
    recoveryUser,
    setCurrentUser,
    isLoggedInRecovery,
    isCodeLocked,
    pin,
    token,
  ]);

  const handleSubmit = (event) => {
    event.preventDefault();

    if (step === FORGOT_PIN_STEP.CHANNEL) {
      handleRequestCode();
      return;
    }

    if (code.length === CODE_LENGTH) {
      handleConfirmCode(code);
    }
  };

  const handleBack = useCallback(() => {
    if (step === FORGOT_PIN_STEP.CODE) {
      clearCodeError();
      setIsCodeLocked(false);
      setCode('');
      setStep(FORGOT_PIN_STEP.CHANNEL);
      return;
    }

    navigate(isLoggedInRecovery ? backPath : '/unlock');
  }, [backPath, clearCodeError, isLoggedInRecovery, navigate, step]);

  return (
    <PinPage className="forgot-pin">
      <input
        ref={keyboardPrimeInputRef}
        className="forgot-pin__keyboard-prime"
        type="text"
        inputMode="numeric"
        autoComplete="off"
        aria-hidden="true"
        tabIndex={-1}
        onChange={(event) => {
          event.target.value = '';
        }}
      />

      <ScreenHeader title={pin.forgotPin} onBack={handleBack} />

      <form id="forgot-pin-form" className="forgot-pin__body" onSubmit={handleSubmit}>
        {step === FORGOT_PIN_STEP.CHANNEL ? (
          <>
            <div className="forgot-pin__intro">
              <h1 className="forgot-pin__title">{pin.recoverAccess}</h1>
              <p className="forgot-pin__subtitle">{pin.chooseCodeChannel}</p>
            </div>

            {availableChannels.length > 0 ? (
              <div className="forgot-pin__channels" role="radiogroup" aria-label={pin.codeChannelLabel}>
                {availableChannels.map((channel) => {
                  const channelConfig = VERIFICATION_CHANNEL_MAP[channel];
                  const channelCopy = channels[channel] || channelConfig;
                  const Icon = channelConfig.Icon;
                  const isSelected = selectedChannel === channel;

                  return (
                    <button
                      key={channel}
                      type="button"
                      className={[
                        'forgot-pin__channel',
                        isSelected ? 'forgot-pin__channel--selected' : null,
                        isChannelSelectionDisabled ? 'forgot-pin__channel--disabled' : null,
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      role="radio"
                      aria-checked={isSelected}
                      aria-disabled={isChannelSelectionDisabled}
                      disabled={isChannelSelectionDisabled}
                      onClick={() => handleSelectChannel(channel)}
                    >
                      <span className="forgot-pin__channel-icon" aria-hidden="true">
                        <Icon />
                      </span>
                      <span className="forgot-pin__channel-text">
                        <span className="forgot-pin__channel-label">{channelCopy.label}</span>
                        <span className="forgot-pin__channel-description">
                          {channelCopy.description}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>
            ) : (
              <p className="forgot-pin__notice" role="alert">
                {pin.noRecoveryChannel}
              </p>
            )}
          </>
        ) : (
          <>
            <div className="forgot-pin__intro">
              <h1 className="forgot-pin__title">{pin.enterCode}</h1>
              <p className="forgot-pin__subtitle">
                {selectedTarget
                  ? t('pin.codeSentVia', {
                    channel: selectedChannelLabel,
                    target: selectedTarget,
                  })
                  : t('pin.codeSent', { target: selectedChannelLabel })}
              </p>
            </div>

            <ForgotPinCodeInput
              ariaLabel={pin.verificationCode}
              value={code}
              disabled={isCodeLocked}
              readOnly={isCheckingCode}
              hasError={isCodeInvalidHighlighted}
              errorKey={codeErrorPulse}
              onChange={handleCodeChange}
              onComplete={handleConfirmCode}
            />

            {isCodeLocked && (
              <p className="forgot-pin__notice forgot-pin__notice--code" role="alert">
                {pin.tooManyCodeAttempts}
              </p>
            )}

            <button
              type="button"
              className="forgot-pin__resend"
              disabled={isResendDisabled}
              onPointerDownCapture={(event) => {
                if (isResendDisabled) {
                  return;
                }

                event.preventDefault();
                primeCodeKeyboard();
                requestCode(selectedChannel);
              }}
              onTouchStartCapture={(event) => {
                if (isResendDisabled) {
                  return;
                }

                event.preventDefault();
                primeCodeKeyboard();
                requestCode(selectedChannel);
              }}
              onMouseDownCapture={(event) => {
                if (isResendDisabled) {
                  return;
                }

                event.preventDefault();
                primeCodeKeyboard();
                requestCode(selectedChannel);
              }}
              onClick={() => {
                primeCodeKeyboard();
                requestCode(selectedChannel);
              }}
            >
              {isRequestingCode
                ? common.sending
                : passwordRequestCooldown.isCooldownActive
                  ? t('pin.resendIn', { time: cooldownText })
                  : pin.resendCode}
            </button>
          </>
        )}
      </form>

      {step === FORGOT_PIN_STEP.CHANNEL && (
        <BottomActionButton
          className="forgot-pin__cta"
          type="button"
          disabled={!canRequestCode}
          onPointerDownCapture={handleRequestCodeGesture}
          onTouchStartCapture={handleRequestCodeGesture}
          onMouseDownCapture={handleRequestCodeGesture}
          onClick={handleRequestCode}
        >
          {isRequestingCode
            ? common.sending
            : passwordRequestCooldown.isCooldownActive
              ? t('pin.sendCodeIn', { time: cooldownText })
              : pin.sendCode}
        </BottomActionButton>
      )}
    </PinPage>
  );
}

export default ForgotPin;
